import { css } from '@emotion/react';
import type { ReactNode } from 'react';

import { formatDateTime } from '@kaiizen/kai-presenters';

import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import { Button, ButtonColorStyle } from '@/Client/Widgets';
import { useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { ChangeOrderIcon, ProgressIcon, PunchListIcon, ScopeIcon } from '../Icons';
import { ScanStatusPill } from './ScanStatusPill';
import { StatusPill } from './StatusPill';

const ARTIFACT_STATUS_CARD_CONTAINER_STYLE = css`
  label: ARTIFACT_STATUS_CARD_CONTAINER_STYLE;
  flex: 1;
  min-width: 0;
  background: var(--background);
  padding: ${LAYOUT2.spacing2}px;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing}px;
  border: 1px solid var(--border1);
`;

const DISABLED_CONTAINER_STYLE = css`
  label: NO_ACTION_CONTAINER_STYLE;
  color: var(--text4);
  border: 1px solid var(--border2);
`;

const STATUS_TITLE_STYLE = css`
  label: STATUS_TITLE_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  ${TYPOGRAPHY2.largeBold}
  height: ${LAYOUT2.spacing3}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const STATUS_DATE_STYLE = css`
  label: STATUS_DATE_STYLE;
  ${TYPOGRAPHY2.xSmall}
  opacity: 0.67;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type CardType = 'scope' | 'changeOrder' | 'punchList' | 'progress';

const CARD_COLORS: Record<CardType, 'charcoal' | 'darkOrange' | 'darkBlue' | 'darkGreen'> = {
  scope: 'charcoal',
  changeOrder: 'darkOrange',
  punchList: 'darkBlue',
  progress: 'darkGreen',
};

const CARD_ICONS: Record<CardType, (props: { color?: string }) => React.ReactNode> = {
  scope: ScopeIcon,
  changeOrder: ChangeOrderIcon,
  punchList: PunchListIcon,
  progress: ProgressIcon,
};

const STATUS_PILLS_STYLE = css`
  label: STATUS_PILLS_STYLE;
  display: flex;
  align-items: center;
`;

const STATUS_BUTTONS_STYLE = css`
  label: STATUS_BUTTONS_STYLE;
  display: flex;
  width: 100%;
  gap: ${LAYOUT2.spacing1}px;
  align-items: center;
`;

export const ArtifactStatusCard = ({
  type,
  action,
  title,
  buttonTitle,
  secondaryButton,
  secondaryButtonContent,
  isAddendum = false,
}: {
  title?: string;
  type: CardType;
  action?: false | (() => void);
  buttonTitle?: string;
  secondaryButton?: { title: string; onClick: () => void; buttonStyle?: ButtonColorStyle };
  secondaryButtonContent?: ReactNode;
  isAddendum?: boolean;
}) => {
  const { scan, latestApprovedScan, project } = useScanEditingContextSlice(['scan', 'latestApprovedScan', 'project']);
  const active = !!action;
  const projectActive = project?.status !== 'completed';
  const showScan = type === 'scope' || (type === 'progress' && latestApprovedScan?.scanType === 'progressCompleted')
    ? latestApprovedScan ?? scan
    : active
    ? scan
    : undefined;
  const date = showScan?.approvedAt ?? showScan?.updatedAt;

  const colorName = CARD_COLORS[type];
  const color = COLORS2.alwaysDark[colorName];
  const Icon = CARD_ICONS[type];

  return (
    <div css={[ARTIFACT_STATUS_CARD_CONTAINER_STYLE, active ? { border: `2px solid ${color}` } : DISABLED_CONTAINER_STYLE]}>
      <div css={[STATUS_TITLE_STYLE]}>
        <Icon />
        {title}
      </div>
      <div css={STATUS_DATE_STYLE}>
        {active && date ? (showScan?.approvedAt ? 'Approved' : 'Last updated') + ': ' + formatDateTime(date) : <br />}
      </div>
      <div css={STATUS_PILLS_STYLE}>
        {isAddendum && <StatusPill small statusTypeText='ADDENDUM' color='charcoal' tight />}
        {active && projectActive
          ? <ScanStatusPill small status={showScan?.scanLifecycleStatus} color={colorName} tight />
          : <StatusPill small />}
      </div>
      <div css={STATUS_BUTTONS_STYLE}>
        {action && (
          <Button
            buttonStyle={colorName}
            disabled={!action}
            onClick={action}
            flex={1}
          >
            {buttonTitle}
          </Button>
        )}
        {secondaryButtonContent}
        {!secondaryButtonContent && secondaryButton && (
          <Button
            buttonStyle={secondaryButton.buttonStyle ?? 'darkBlue'}
            onClick={secondaryButton.onClick}
            flex={1}
          >
            {secondaryButton.title}
          </Button>
        )}
      </div>
    </div>
  );
};
